import {
  collection,
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import { ulid } from 'ulid';
import { getDb } from '@talk2nick/shared';
import {
  formatDateTimeShort,
  MAX_GROUP_MEMBERS,
  type ConvoType,
  type MemberRole,
} from '@talk2nick/core';
import { updateUserProfileCache } from './useUserProfiles';

/**
 * 대화방 생성 (PLAN 0-B 변경 9장).
 *
 * 친구 요청이 사라졌으므로 **방은 "나 혼자"로 만들어진다.** 상대는 티켓을 clam해야
 * 비로소 `memberIds`에 추가된다 — 1:1 대화도 "2명이 있는 방"으로 취급한다.
 *
 * 쓰기 순서는 Rules가 강제한다 (배치로 묶을 수 없다 — 규칙의 get()이 배치 이전 상태를 본다):
 *  1) `convos/{ulid}` 생성 (생성자가 memberIds에 포함)
 *  2) `members/{나}` — 아직 멤버가 아니라 `convoOwner == uid && role == 'owner'`
 *     부트스트랩 경로로만 성립한다
 *
 * 문서 ID는 ULID다. 예전 DM처럼 `uidA_uidB`를 쓰지 않는 이유는, 티켓 발급 전에
 * 상대 uid를 알 수 없기 때문이다. `dmKey`는 중복 방지용이 아니라 "문서 ID와 같은
 * 안정 키"로만 쓰인다 — 상대는 티켓 clam 시점에 정해진다.
 */

export interface CreateRoomInput {
  type: ConvoType;
  /** 그룹명 (DM은 비워 둔다 — 상대 프로필로 실시간 계산한다) */
  title?: string;
}

export async function createInviteRoom(myUid: string, input: CreateRoomInput): Promise<string> {
  const db = getDb();
  const convoId = ulid();
  const title = input.title?.trim();

  await setDoc(doc(db, 'convos', convoId), {
    type: input.type,
    ...(title ? { title } : {}),
    createdBy: myUid,
    memberIds: [myUid],
    adminIds: [myUid],
    dmKey: convoId,
    settings: { disappearingTtlSeconds: 0, private: false },
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  await setDoc(doc(db, 'convos', convoId, 'members', myUid), {
    role: 'owner',
    joinedAt: serverTimestamp(),
    lastReadAt: serverTimestamp(),
    unreadCount: 0,
    pinned: false,
    archived: false,
    translation: { mode: 'inherit', updatedAt: serverTimestamp() },
  });

  return convoId;
}

/** 방 목록 정렬용 — 서버 시각이 아직 없는 로컬 값도 다룬다 */
export function timestampMs(value: { toDate(): Date } | undefined | null): number {
  return value ? value.toDate().getTime() : 0;
}

// ── 방장 조작 (PLAN 5-3 / 5-2장, 변경 9장) ─────────────────────────────
//
// 모든 함수가 **쓰기 2회**로 나뉜다. Rules의 get()이 배치 이전 상태를 보기 때문에
// 한 트랜잭션으로 묶을 수 없다: 멤버 문서 → convos.memberIds 순서가 정해져 있다.

function memberDoc(convoId: string, uid: string) {
  return doc(getDb(), 'convos', convoId, 'members', uid);
}

/**
 * 강제 퇴실. 멤버 문서에 `leftAt`/`removedBy`만 남긴다.
 *
 * **`memberIds`에서 자신을 빼지 않는다** — Rules의 `convos` 읽기가 `uid in memberIds`를
 * 요구하므로, 빼는 순간 그 사람은 방 문서도 메시지도 읽지 못해 목록에서 통째로 사라진다.
 * 남겨두므로써 강제퇴장도 "읽기 전용 보관 + 배지"로 남는다(폭파 아카이브와 같은 규칙).
 * 대신 좌석(256 상한)은 이만큼 점유한다.
 *
 * `leftAt`이 활성 여부(`isActiveMember`)를 끄므로 쓰기·역할 판정은 그대로 막힌다.
 */
export async function removeMember(
  convoId: string,
  targetUid: string,
  byUid: string,
): Promise<void> {
  await updateDoc(memberDoc(convoId, targetUid), {
    leftAt: serverTimestamp(),
    removedBy: byUid,
  });
}

/**
 * 강제 퇴실 되돌리기 — `leftAt`/`removedBy`만 지우면 다시 활성 멤버가 된다.
 * `memberIds`는 빠지지 않았으므로 좌석·목록 그대로다.
 */
export async function restoreMember(convoId: string, targetUid: string): Promise<void> {
  await updateDoc(memberDoc(convoId, targetUid), {
    leftAt: deleteField(),
    removedBy: deleteField(),
  });
}

/**
 * 자발적 퇴실 — `leftAt`만 남긴다.
 *
 * 나간 뒤에도 방은 내 목록에 남고 대화 내용을 읽을 수 있다(읽기 전용).
 * `leftAt`은 한 번 쓰면 Rules가 다시 쓰기를 막으므로 두 번째 퇴실은 무시된다.
 */
export async function leaveRoom(convoId: string, uid: string): Promise<void> {
  const snap = await getDoc(memberDoc(convoId, uid));
  if (!snap.exists()) return;
  if ((snap.data() as { leftAt?: unknown }).leftAt) return;
  await updateDoc(memberDoc(convoId, uid), { leftAt: serverTimestamp() });
}

/**
 * 방이 목록에서 사라진 뒤, 내 멤버 문서로 이탈 사유를 판정한다.
 *
 * 멤버 문서는 퇴장 후에도 본인이 읽을 수 있다(Rules의 isSelf(uid)),
 * 폭파 시에는 archiveConvo가 방장분만 남기고 나머지를 지운다.
 *
 * - `left`:    자발적 퇴실 — leftAt만 남았다
 * - `kicked`:  강제퇴장 — leftAt과 removedBy가 함께 남았다
 * - `destroyed`: 멤버 문서가 없거나 퇴장 이력이 없다 — 폭파·삭제·판정 불가
 *                (조회 실패 시에도 기존과 동일하게 폭파 안내로 대체)
 */
export async function getExitKind(
  convoId: string,
  uid: string,
): Promise<'left' | 'kicked' | 'destroyed'> {
  try {
    const snap = await getDoc(memberDoc(convoId, uid));
    if (!snap.exists()) return 'destroyed';
    const data = snap.data() as { leftAt?: unknown; removedBy?: unknown };
    if (data.leftAt && data.removedBy) return 'kicked';
    if (data.leftAt) return 'left';
    return 'destroyed';
  } catch (err) {
    console.warn('[talk2nIcK] 이탈 사유 판정 실패, 폭파 안내로 대체:', err);
    return 'destroyed';
  }
}

/**
 * 역할 변경 — `members/{uid}.role`과 `convos.adminIds`를 함께 맞춘다.
 * `adminIds`는 비정규화 필드라 두 군데가 어긋나면 권한 판정이 꼬인다.
 *
 * `updatedBy`를 반드시 남긴다 — Functions의 `onGroupAdminAction`이 이 필드로
 * "누가 권한을 바꿨는지"를 읽어 감사 로그를 남긴다. 빠지면 조용히 감사되지 않는다.
 */
export async function setMemberRole(
  convoId: string,
  targetUid: string,
  role: MemberRole,
  currentAdminIds: string[],
  actorUid: string,
): Promise<void> {
  await updateDoc(memberDoc(convoId, targetUid), { role, updatedBy: actorUid });
  const next = new Set(currentAdminIds);
  if (role === 'admin') next.add(targetUid);
  else next.delete(targetUid);
  await updateDoc(doc(getDb(), 'convos', convoId), {
    adminIds: [...next].sort(),
    updatedAt: serverTimestamp(),
  });
}

/** 방 이름 변경 */
export async function setConvoTitle(convoId: string, title: string): Promise<void> {
  const trimmed = title.trim();
  await updateDoc(doc(getDb(), 'convos', convoId), {
    title: trimmed || deleteField(),
    updatedAt: serverTimestamp(),
  });
}

/** 참여자 별명(alias) 설정 / 변경 (Admin 전용) */
export async function setMemberAlias(
  convoId: string,
  targetUid: string,
  alias: string,
): Promise<void> {
  const trimmed = alias.trim();
  const db = getDb();
  await updateDoc(doc(db, 'convos', convoId), {
    [`memberAliases.${targetUid}`]: trimmed || deleteField(),
    updatedAt: serverTimestamp(),
  });
  await updateDoc(doc(db, 'convos', convoId, 'members', targetUid), {
    alias: trimmed || deleteField(),
    updatedAt: serverTimestamp(),
  });
}

/**
 * 사용자가 자신의 대화명(Display Name / Nickname)을 변경 (사용자 별 각자 대화명 변경 기능).
 * 1) users/{uid}.displayName 갱신
 * 2) convoId가 전달된 경우 convos/{convoId}/members/{uid}.nickname 갱신
 * 3) 인메모리 프로필 캐시 동기화
 */
export async function updateMyDisplayName(
  uid: string,
  newDisplayName: string,
  convoId?: string,
): Promise<void> {
  const trimmed = newDisplayName.trim();
  if (!trimmed) throw new Error('name-empty');
  if (trimmed.length > 30) throw new Error('name-too-long');

  const db = getDb();
  const tasks: Promise<unknown>[] = [
    updateDoc(doc(db, 'users', uid), {
      displayName: trimmed,
      updatedAt: serverTimestamp(),
    }),
  ];

  if (convoId) {
    tasks.push(
      updateDoc(doc(db, 'convos', convoId, 'members', uid), {
        nickname: trimmed,
        updatedAt: serverTimestamp(),
      }),
    );
  }

  await Promise.all(tasks);
  updateUserProfileCache(uid, { displayName: trimmed });
}

/** 사라지는 메시지 TTL 변경 (0 = 끔). 점 경로로 써야 다른 settings 필드를 지우지 않는다. */
export async function setConvoTtl(
  convoId: string,
  ttlSeconds: 0 | 86400 | 604800 | 7776000,
): Promise<void> {
  await updateDoc(doc(getDb(), 'convos', convoId), {
    'settings.disappearingTtlSeconds': ttlSeconds,
    updatedAt: serverTimestamp(),
  });
}

/**
 * 인시더리트(PLAN 5-10장) — 모바일 데이터 자동다운로드 차단.
 *
 * 방 단위 스위치다. 사용자 전역 스위치(`privacy.autoDownloadOnCellular`)와 별개로,
 * 방마다 다르게 지정할 수 있어야 한다(PLAN 6-8장 표).
 */
export async function setConvoPrivate(convoId: string, isPrivate: boolean): Promise<void> {
  await updateDoc(doc(getDb(), 'convos', convoId), {
    'settings.private': isPrivate,
    updatedAt: serverTimestamp(),
  });
}

/** 좌석이 남았는가 (그룹 256명 상한) */
export function canAddMember(currentCount: number): boolean {
  return currentCount < MAX_GROUP_MEMBERS;
}

/**
 * 대화방 및 하위 컬렉션 삭제 (Admin 전용)
 */
export async function deleteConvo(convoId: string): Promise<void> {
  const db = getDb();
  const subcollections = ['members', 'tickets', 'messages', 'pinned', 'settings'];
  for (const sub of subcollections) {
    try {
      const snap = await getDocs(collection(db, 'convos', convoId, sub));
      if (!snap.empty) {
        await Promise.all(snap.docs.map((d) => deleteDoc(d.ref)));
      }
    } catch (err) {
      console.warn(`[talk2nIcK] Failed to delete subcollection ${sub} for convo ${convoId}:`, err);
    }
  }
  await deleteDoc(doc(db, 'convos', convoId));
}

/**
 * 대화방 일괄 삭제 (Admin 전용)
 */
export async function deleteConvosBulk(convoIds: string[]): Promise<void> {
  await Promise.all(convoIds.map((cid) => deleteConvo(cid)));
}

/**
 * 대화방 즉시 폭파 (Admin 전용) — **완전 삭제가 아니라 방장만 남는 아카이브**.
 *
 * 메시지·고정·방 설정은 그대로 두고, 방장(memberIds = [destroyedBy])만 남긴다.
 * 다른 멤버는 `memberIds`에서 빠지는 순간 방 문서를 읽지 못하므로 목록에서 사라지고,
 * 남아 있던 멤버 문서가 지워지므로 `getExitKind`가 `destroyed`로 판정한다.
 * 방장은 목록에 남아 폭파 배지 + 읽기 전용으로 대화를 계속 볼 수 있다.
 */
export async function destroyConvoNow(
  convoId: string,
  reason: string,
  destroyedBy: string,
  destroyedByName?: string,
): Promise<void> {
  const db = getDb();
  const notice = {
    reason: reason.trim(),
    destroyedBy,
    destroyedByName: destroyedByName ?? 'nIcK',
    createdAt: Date.now(),
    status: 'executed' as const,
  };

  // 1. 방 문서에 폭파 공지 기록 — 아직 멤버인 라이브 클라이언트가 이 스냅샷을 받아야
  //    곧 있을 memberIds 축소 전에 "방이 폭파됐다"는 사실을 전할 수 있다
  await updateDoc(doc(db, 'convos', convoId), {
    destructionNotice: notice,
    destroyedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  // 2. 시스템 메시지 등록 (실시간 리스너 및 푸시 감지)
  try {
    const msgId = ulid();
    await setDoc(doc(db, 'convos', convoId, 'messages', msgId), {
      type: 'system',
      text: `[방 폭파 안내] 관리자 사유: ${notice.reason}`,
      clientCreatedAt: Date.now(),
      createdAt: serverTimestamp(),
      reactions: {},
      reactionCount: 0,
    });
  } catch (err) {
    console.warn('[talk2nIcK] 폭파 시스템 메시지 등록 실패:', err);
  }

  // 3. 공지 스냅샷이 다른 멤버에게 도달할 시간을 준 뒤 아카이브한다
  await new Promise((resolve) => setTimeout(resolve, ARCHIVE_NOTICE_GRACE_MS));
  await archiveConvo(convoId, destroyedBy);
}

/** 폭파 공지 전달 여유 시간 — 이 사이에 멤버들이 executed 스냅샷을 받는다 */
const ARCHIVE_NOTICE_GRACE_MS = 1200;

/**
 * 방장만 남기는 아카이브 배치.
 *
 * `memberIds` 축소·`adminIds` 정리·남은 멤버 문서 삭제·티켓 회수를 한 배치로 묶는다 —
 * 찢어 놓으면 라이브 클라이언트가 "memberIds는 반으로 줄었는데 남의 멤버 문서가 아직
 * 있다" 같은 절반짜리 방을 받는다. `adminIds`는 비정규화라 지워진 멤버를 가리키면
 * 이후 권한 판정이 흐트러진다.
 */
async function archiveConvo(convoId: string, keepUid: string): Promise<void> {
  const db = getDb();
  const [memberDocs, ticketDocs] = await Promise.all([
    getDocs(collection(db, 'convos', convoId, 'members')),
    getDocs(collection(db, 'convos', convoId, 'tickets')),
  ]);

  const batch = writeBatch(db);
  for (const snap of memberDocs.docs) {
    if (snap.id !== keepUid) batch.delete(snap.ref);
  }
  // 초대 티켓은 방이 끝났으므로 남기면 쓸모 없는 진입 통로가 된다
  for (const snap of ticketDocs.docs) batch.delete(snap.ref);
  // adminIds는 비정규화라 남으면 roleOf()가 지워진 멤버 문서를 읽으러 간다
  batch.update(doc(db, 'convos', convoId), {
    memberIds: [keepUid],
    adminIds: [keepUid],
    updatedAt: serverTimestamp(),
  });
  await batch.commit();
}

/**
 * 대화방 예약 폭파 (Admin 전용)
 */
export async function scheduleDestroyConvo(
  convoId: string,
  reason: string,
  scheduledAtMs: number,
  destroyedBy: string,
  destroyedByName?: string,
): Promise<void> {
  const db = getDb();
  const notice = {
    reason: reason.trim(),
    destroyedBy,
    destroyedByName: destroyedByName ?? 'nIcK',
    createdAt: Date.now(),
    scheduledAt: scheduledAtMs,
    status: 'scheduled' as const,
  };

  await updateDoc(doc(db, 'convos', convoId), {
    destructionNotice: notice,
    updatedAt: serverTimestamp(),
  });

  try {
    // 시각만 있으면 다음 날 일정도 "오늘 09:00"으로 읽힌다 — 날짜까지 붙인다
    const timeStr = formatDateTimeShort(scheduledAtMs);
    const msgId = ulid();
    await setDoc(doc(db, 'convos', convoId, 'messages', msgId), {
      type: 'system',
      text: `[예약 폭파 안내] ${timeStr}에 대화방이 종료 및 삭제될 예정입니다. (사유: ${notice.reason})`,
      clientCreatedAt: Date.now(),
      createdAt: serverTimestamp(),
      reactions: {},
      reactionCount: 0,
    });
  } catch (err) {
    console.warn('[talk2nIcK] 예약 폭파 시스템 메시지 등록 실패:', err);
  }
}

/**
 * 대화방 예약 폭파 취소 (Admin 전용)
 */
export async function cancelScheduledDestroy(convoId: string): Promise<void> {
  const db = getDb();
  await updateDoc(doc(db, 'convos', convoId), {
    destructionNotice: deleteField(),
    updatedAt: serverTimestamp(),
  });
}

/** `datetime-local` 입력값(로컬 시각) → 밀리초. 빈 값·해석 불가는 null */
export function localInputToMs(value: string): number | null {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

/** 밀리초 → `datetime-local` 입력값(로컬 시각, 분 단위) */
export function msToLocalInput(ms: number): string {
  const local = new Date(ms - new Date(ms).getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}
